import React, {
  forwardRef,
  useContext,
  useEffect,
  useState,
} from "react";
import expertDefaultImg from "../../../images/expert-default.png";
import logoWhite from "../../../images/logo_white.png";
import industryLogo from "../../../images/default_industry.png";
import { motion } from "framer-motion";
import { UrlContext } from "../../../contexts/UrlContext";
import ScreeningQuestionsAnswer from "./screeningQuestionsAnswer";
import { DayPilot } from "@daypilot/daypilot-lite-react";
import RadarChart from "./radarChart";
import Swal from "sweetalert2";

const ProfilePrintContent = forwardRef(function ProfilePrintContent(
  { expertID, projectID, profile, projectType, renderScope = "default", clickingOnProfile },
  ref
) {
  const { API_URL } = useContext(UrlContext);
  const mediaLink = "https://media.licdn.com/";
  const databaseLink = "https://images.expertpowerhouse.com";
  const [profileExperience, setProfileExperience] = useState([]);
  const [dimension, setDimension] = useState([]);
  const [events, setEvents] = useState([]);
  const profileName = projectType === "exp" ? "EPH Expert" : "EPH Consultant";
  const expertPosition = profile?.expertPosition || "Independent Consultant";

  const profilePicturePreview = profile?.expertProfilePicture?.includes(
    mediaLink
  )
    ? profile.expertProfilePicture
    : `${databaseLink}${profile?.expertProfilePicture || ""}`;

  const location = profile?.expertLocation || "";
  const languages = profile?.language || "";
  const profileEducation = profile?.expertEducation || [];
  const rateCurrency = profile?.projectCurrency;
  const rateValue = profile?.expertDailyRate;
  const rateFrequency = profile?.expertBillingFrequency;
  const ephExpert = projectType === "exp" ? "EPH_EXP_" : "EPH_CON_";
  const availabilityComment = profile?.expertAvailableComment || "Fully available";
  const profileSummary = profile?.expertSum || "";
	const [selectedSlots, setSelectedSlots] = useState([]);
	const [loading, setLoading] = useState(false);

	const toggleSelectedSlot = (slotId) => {
		if (!slotId) return;
		setSelectedSlots((prev) =>
			prev.includes(slotId)
				? prev.filter((id) => id !== slotId)
				: [...prev, slotId]
		);
	};

  const eventToString = (selectedEvent) => {
    const selectedTimeSlot = {
      weekday: new Intl.DateTimeFormat("en-GB", { weekday: "short" }).format(
        new Date(selectedEvent.startTime)
      ),
      day: new Intl.DateTimeFormat("en-GB", { day: "2-digit" }).format(
        new Date(selectedEvent.startTime)
      ),
      month: new Intl.DateTimeFormat("en-GB", { month: "short" }).format(
        new Date(selectedEvent.startTime)
      ),
      year: new Intl.DateTimeFormat("en-GB", { year: "numeric" }).format(
        new Date(selectedEvent.startTime)
      ),
      startTime: new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(selectedEvent.startTime)),
      endTime: new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(selectedEvent.endTime)),
    };

    return `${selectedTimeSlot.weekday}, ${selectedTimeSlot.day} ${selectedTimeSlot.month} ${selectedTimeSlot.year} ${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime} (${selectedEvent.timezone})`;
  };

  const getExpertExperience = async () => {
    const requestBody = {
      expertID,
      powerPointID: profile?.ID,
    };

    try {
      const response = await fetch(`${API_URL}fetch-power-point-experience`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        setProfileExperience(responseData.expertExperience || []);
      } else {
        console.error("Error fetching expert experience:", responseData.text);
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
    }
  };

  const getProjectDimensions = async () => {
    const requestBody = { projectID };

    try {
      const response = await fetch(`${API_URL}get-current-project-dimensions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const fetchedDimensions = [
          responseData.dimensions?.dimension1,
          responseData.dimensions?.dimension2,
          responseData.dimensions?.dimension3,
          responseData.dimensions?.dimension4,
          responseData.dimensions?.dimension5,
        ];

        setDimension(fetchedDimensions);
      } else {
        console.error("Failed to get dimensions");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  // API to fetch todays and future timeslots
  const getMeetingSlots = async () => {
    const requestBody = {
      projectID,
      expertID,
    };

    try {
      const response = await fetch(`${API_URL}get-expert-meeting-time-slot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const formattedEvents = responseData.eventData
          .filter((event) => {
            const eventDate = new Date(event.startTime);
            eventDate.setHours(0, 0, 0, 0);

            return eventDate >= today;
          })
          .sort(
            (a, b) =>
              new Date(a.startTime).getTime() -
              new Date(b.startTime).getTime()
          )
          .map((event) => ({
            id: event.id,
            eventID: event.eventID,
            expertID: event.expertID,
            projectID: event.projectID,
            timezone: event.timezone,
            startTime: new DayPilot.Date(event.startTime),
            endTime: new DayPilot.Date(event.endTime),
            start: new DayPilot.Date(event.startTime),
            end: new DayPilot.Date(event.endTime),
            text: eventToString(event),
            isCallRequested: event.isCallRequested,
            callRequestDate: event.callRequestDate,
          }));

        setEvents(formattedEvents);
      } else {
        console.error("Failed to get meeting slots");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

	// API for fetching meeting timeslots
	const requestForCall = async () => {
		setLoading(true);
		const requestBody = {
			projectID: projectID,
			expertID: expertID,
			selectedSlots: selectedSlots,
      client: true,
		};
		try {
			let modifiedUrl = "request-for-call";
			const response = await fetch(`${API_URL}${modifiedUrl}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(requestBody),
			});
			const responseData = await response.json();
			if (responseData.status === "success") {
        clickingOnProfile(expertID, 'Client requested for a call.')
				const currentTime = new Date().toISOString().slice(0, 19);
				setEvents((prevEvents) =>
					prevEvents.map((event) =>
						selectedSlots.includes(event.id)
							? {
									...event,
									isCallRequested: true,
									callRequestDate: currentTime,
								}
							: event
					)
				);
				Swal.fire({
					title: "Thank you!",
					text: "We will schedule a call as soon as posible.",
					icon: "success",
					confirmButtonText: "OK",
					timer: 5000,
					timerProgressBar: true,
				});
			} else {
				Swal.fire({
					title: "Error!",
					text: "Error while requesting call.",
					icon: "error",
					confirmButtonText: "OK",
					timer: 2500,
					timerProgressBar: true,
				});
			}
		} catch (error) {
			Swal.fire({
				title: "Error!",
				text: "Error while requesting call.",
				icon: "error",
				confirmButtonText: "OK",
				timer: 2500,
				timerProgressBar: true,
			});
		} finally {
			setLoading(false);
			setSelectedSlots([]);
		}
	};

  useEffect(() => {
    if (!expertID) return;

    getExpertExperience();
    getProjectDimensions();
    getMeetingSlots();
  }, [expertID, projectID]);

  function useBubbleCount() {
    const [count, setCount] = useState(0);

    useEffect(() => {
      const updateCount = () => {
        const width = window.innerWidth;
        const bubbleCount = Math.max(8, Math.floor(width / 52));

        setCount(bubbleCount);
      };

      updateCount();
      window.addEventListener("resize", updateCount);

      return () => window.removeEventListener("resize", updateCount);
    }, []);

    return count;
  }

  const baseCount = useBubbleCount();
  const rowOffsets = [0, 2, 3];

  const getRowCount = (rowIndex) => {
    return Math.max(baseCount - (rowOffsets[rowIndex] || 0), 0);
  };

  const renderSummarySection = () => (
    <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow print-card-outer print-no-shadow print-border">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-bold print-text-lg">EPH Summary</h2>
      </div>

      <div
        className="prose prose-sm text-gray-700 text-sm leading-relaxed print-text-sm [&_h3]:text-md [&_h3]:mt-2 [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
        dangerouslySetInnerHTML={{ __html: profileSummary }}
      />
    </div>
  );

  const renderSpiderChartSection = () => {
    if (!profile?.hasMatching) return null;

    return (
      <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow print-card-outer print-no-shadow print-border">
        <h2 className="text-xl font-bold mb-2 sm:mb-0 print-text-lg">
          EPH Requirement Matching
        </h2>

        <div className="flex flex-col sm:flex-row gap-6 items-center justify-between">
          <div className="w-full lg:w-5/12">
            {[1, 2, 3, 4, 5].map((num) => {
              const key = num - 1;

              return (
                <div
                  key={num}
                  className="flex items-start gap-2 text-sm text-[#365561] print-text-sm"
                >
                  <span className="shrink-0">{num}:</span>
                  <span className="leading-5 break-words">
                    {dimension?.[key] || "-"}
                  </span>
                </div>
              );
            })}
          </div>

          <RadarChart
            values={profile?.chartConfig?.value || []}
            labels={["1", "2", "3", "4", "5"]}
            valueBadge={profile?.chartConfig?.badgeValue}
            size={850}
            renderSize={150}
          />
        </div>
      </div>
    );
  };

  const renderScreeningQuestionsSection = () => (
    <ScreeningQuestionsAnswer
      project_id={projectID}
      expert_id={profile?.expertID}
      profile={profile}
    />
  );

  const renderExperienceSection = () => (
		<motion.div
			key="experience-view"
			layout
			initial={{ x: 0, opacity: 1 }}
			animate={{ x: 0, opacity: 1 }}
			exit={{ x: "100%", opacity: 0 }}
			transition={{ type: "spring", stiffness: 500, damping: 30 }}
			className="bg-white rounded-xl shadow p-6 relative print-experience-split print-no-shadow print-border"
		>
      <h2 className="text-xl font-bold mb-6 print-text-lg">
        Relevant Experience
      </h2>

      {profileExperience?.length > 0 ? (
				<div className="space-y-2 print-experience-list">
					{profileExperience.map((exp, index) => (
						<div
							key={exp.id || index}
							className="relative w-full rounded-md transition duration-200 px-2 py-1 select-none print-experience-item"
						>
              {exp.subExperienceBlocks?.length > 0 ? (
                <>
                  <div className="flex ml-1 items-start gap-4">
                    {exp.companyLogo ? (
                      <img
                        src={
                          exp.companyLogo.includes("/pictures/")
                            ? `https://images.expertpowerhouse.com${exp.companyLogo}`
                            : exp.companyLogo
                        }
                        alt="Company Logo"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = industryLogo;
                        }}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                      />
                    ) : (
                      <img
                        src={industryLogo}
                        alt="Default Industry Logo"
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                      />
                    )}

                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base print-text-md">
                        {exp.companyName}
                      </h3>
                      <p className="text-sm text-gray-500 print-text-sm">
                        {exp.duration}
                      </p>
                    </div>
                  </div>

                  <ul className="list-none mt-2 ml-9 pl-4 relative">
                    {exp.subExperienceBlocks.map((role, j) => (
                      <li
                        key={j}
                        className={`relative pl-2 ${
                          j === exp.subExperienceBlocks.length - 1
                            ? ""
                            : "mb-3"
                        }`}
                      >
                        <div className="absolute -left-[35px] top-2 w-2 h-2 bg-gray-400 rounded-full z-10" />

                        {j !== exp.subExperienceBlocks.length - 1 && (
                          <div className="absolute -left-[32px] top-5 h-full w-px bg-gray-300 z-0" />
                        )}

                        <h4 className="text-gray-800 font-medium text-sm sm:text-base print-text-md">
                          {role.designation}
                        </h4>

                        <p className="text-xs text-gray-500 print-text-sm">
                          {role.joiningDate} {role.leavingDate ? "-" : null}{" "}
                          {role.leavingDate} {role.duration ? "·" : null}{" "}
                          {role.duration}
                        </p>

                        {role.location && (
                          <p className="text-xs text-gray-500 print-text-sm">
                            {role.location}
                          </p>
                        )}

                        {role.description && (
                          <p className="mt-1 text-sm text-gray-700 whitespace-pre-line print-text-sm">
                            {role.description}
                          </p>
                        )}

                        {role.skills && (
                          <p className="mt-2 text-xs text-gray-600 font-medium print-text-sm">
                            💡{" "}
                            {Array.isArray(role.skills)
                              ? role.skills.join(", ")
                              : role.skills}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="ml-1 flex gap-4">
                  {exp.companyLogo ? (
                    <img
                      src={
                        exp.companyLogo.includes("/pictures/")
                          ? `https://images.expertpowerhouse.com${exp.companyLogo}`
                          : exp.companyLogo
                      }
                      alt="Company Logo"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = industryLogo;
                      }}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                    />
                  ) : (
                    <img
                      src={industryLogo}
                      alt="Default Industry Logo"
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                    />
                  )}

                  <div>
                    <h4 className="text-gray-800 font-medium text-sm sm:text-base print-text-md">
                      {exp.designation}
                    </h4>

                    <p className="text-sm text-gray-500 print-text-sm">
                      {exp.companyName}
                    </p>

                    <p className="text-xs text-gray-500 print-text-sm">
                      {exp.joiningDate} {exp.leavingDate ? "-" : null}{" "}
                      {exp.leavingDate} {exp.duration ? "·" : null}{" "}
                      {exp.duration}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 print-text-sm">
          No experience data available.
        </p>
      )}
    </motion.div>
  );

  const renderEducationSection = () => (
    <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow print-card-outer print-no-shadow print-border">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-bold print-text-lg">
          Education & Certification
        </h2>
      </div>

      {profileEducation?.length > 0 ? (
        <div className="space-y-4">
          {profileEducation.map((block, index) => {
            const lines = block.split("\n").filter(Boolean);

            return (
              <div key={index}>
                <h4 className="text-gray-800 font-medium text-sm sm:text-base print-text-md">
                  {lines[0]}
                </h4>

                {lines.slice(1).length > 0 && (
                  <p className="text-sm text-gray-500 print-text-sm">
                    {lines.slice(1).join(" · ")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 print-text-sm">
          No education data available.
        </p>
      )}
    </div>
  );

  const renderHeaderSection = () => (
    <div className="relative bg-white rounded-xl shadow-sm z-40 print-safe-reset">
      <div className="print-banner relative h-24 sm:h-36 bg-cover bg-top bg-[24%_0%] sm:bg-[23%_0%] rounded-xl print-rounded-none">
        <div className="absolute inset-x-0 top-0 h-24 sm:h-36 rounded-t-xl bg-gradient-to-r from-[#365561] via-[#2f4a55] to-[#0f172a]" />

        {[0, 1, 2].map((row) => (
          <div
            key={row}
            style={{ "--row": row }}
            className="
              print-hide-bubbles
              absolute inset-0 flex flex-wrap 
              justify-end gap-3 sm:gap-4 px-4 opacity-80
              top-[calc(24px+var(--row)*24px)]
              sm:top-[calc(24px+var(--row)*40px)]
            "
          >
            {[...Array(getRowCount(row))].map((_, i) => {
              const isHighlight = i % 7 === 0 || i % 11 === 0;

              return (
                <div
                  key={i}
                  className={`rounded-full ${
                    isHighlight ? "bg-[#2dd4bf]" : "bg-[#3b6a75]"
                  } w-[10px] h-[10px] sm:w-[16px] sm:h-[16px]`}
                />
              );
            })}
          </div>
        ))}

        <div className="absolute top-2 left-4 sm:top-4 sm:left-4 text-white text-xl p-2 rounded-full transition-transform duration-200 ease-in-out">
          <img
            src={logoWhite}
            alt="EPH Logo"
            className="w-[90px] sm:w-[180px] print-eph-logo"
          />
        </div>
      </div>

      <div className="px-4 sm:px-6 md:px-8 print-px-reset">
        <div className="relative flex flex-col items-left text-left -mt-20 sm:-mt-24 print-mt-reset">
          <div className="mt-2 flex flex-col mb-4">
            <div className="flex flex-col">
              <div className="relative w-24 h-24 sm:w-36 sm:h-36 mt-4 print-profile-image print-profile-side">
                <div className="relative group mx-auto w-24 h-24 sm:w-36 sm:h-36 mb-4 bg-gray-100 rounded-full border-4 border-white text-gray-400 print-profile-image">
                  <img
                    src={profilePicturePreview || expertDefaultImg}
                    alt="Preview"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = expertDefaultImg;
                    }}
                    className="w-full h-full rounded-full border-5 border-white object-cover object-[center_15%] shadow-md"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-start md:justify-between items-start lg:items-center gap-4 print-header-row">
              <div className="flex flex-col print-header-left print-info-side">
                <div className="flex flex-row items-center justify-start gap-2">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 no-underline mb-1 print-text-xl">
                    {profile?.isShareName
                      ? profileName
                      : `${ephExpert}${profile?.expertDigitalID || ""}`}
                  </p>
                </div>

                <div className="flex flex-col gap-2 text-sm sm:text-md mb-2 print-text-md">
                  <div className="flex flex-col">
                    <div className="flex items-start gap-1 max-w-xs sm:max-w-sm md:max-w-md mb-0.5">
                      <span className="leading-5">💼</span>
                      <span className="leading-5 break-words whitespace-normal">
                        {expertPosition}
                      </span>
                    </div>

                    <div className="flex items-start gap-1 max-w-[400px] mb-0.5">
                      <span className="leading-5">🌍 </span>
                      <span className="leading-5 break-words whitespace-normal">
                        {location}
                      </span>
                    </div>

                    <div className="flex items-start gap-1 max-w-xs sm:max-w-sm md:max-w-md mb-0.5">
                      <span className="leading-5">🗣️</span>
                      <span className="leading-5 break-words whitespace-normal">
                        {languages || "No information"}
                      </span>
                    </div>

                    <div className="flex items-start gap-1 max-w-[400px] mb-0.5">
                      <span className="leading-5">📆</span>
                      <span className="leading-5 break-words whitespace-normal">
                        {availabilityComment}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-start md:justify-end mb-4 print-rate-wrap print-rate-side">
                <div className="bg-[#365561] text-white rounded-xl px-6 py-4 shadow-lg min-w-[200px] print-no-shadow print-rate-card">
                  <div className="text-[10px] sm:text-xs uppercase tracking-wide opacity-80">
                    rate
                  </div>

									<span className="inline-flex items-baseline gap-2 uppercase">
										<p className="mt-1 text-xl sm:text-3xl font-bold leading-tight print-text-xl">{rateCurrency} {rateValue}</p>
										<p className="text-[10px] sm:text-xs opacity-80">{rateFrequency}</p>
									</span>

									<p className="mt-1 text-[10px] sm:text-xs opacity-80">incl. of all EPH fees</p>
                </div>
              </div>
            </div>

						<div
							hidden={events.length === 0}
							className="flex flex-col items-start gap-1 print-availability"
						>
							<span className="pt-1 text-sm text-gray-500 font-semibold">Availability for Call (Europe/Berlin):</span>
							
							<div className="grid grid-cols-[repeat(auto-fill,85px)] gap-1.5 w-full bg-white availability-grid">
								{events.map((event, index) => {
									const startDate = new Date(event.startTime);
									const endDate = new Date(event.endTime);

									const weekday = new Intl.DateTimeFormat("en-GB", {
										weekday: "short",
									}).format(startDate);

									const day = new Intl.DateTimeFormat("en-GB", {
										day: "2-digit",
									}).format(startDate);

									const month = new Intl.DateTimeFormat("en-GB", {
										month: "short",
									}).format(startDate);

									const yearShort = new Intl.DateTimeFormat("en-GB", {
										year: "2-digit",
									}).format(startDate);

									const startTime = new Intl.DateTimeFormat("en-GB", {
										hour: "2-digit",
										minute: "2-digit",
										hour12: false,
									}).format(startDate);

									const endTime = new Intl.DateTimeFormat("en-GB", {
										hour: "2-digit",
										minute: "2-digit",
										hour12: false,
									}).format(endDate);

									const isSelected = selectedSlots.includes(event.id);

									return (
										<div
											key={event.id || index}
											onClick={event.isCallRequested ? undefined : () => toggleSelectedSlot(event.id)}
											className={`
												availability-card relative
												w-[85px] h-[82px]
												min-w-[72px] max-w-[85px]
												overflow-hidden rounded-xl
												border bg-[#365561]/5
												shadow-sm
												text-left cursor-pointer
												transition-all duration-150
												hover:-translate-y-0.5 hover:shadow-md

												${
													isSelected
														? "border-[#365561] ring-2 ring-[#365561]/30"
														: "border-[#365561]/20"
												}
											`}
											aria-pressed={isSelected}
										>
											<div className="bg-[#365561] px-1 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-white availability-card-top">
												{month} {yearShort}
											</div>

											<div className="px-2 py-1 text-center availability-card-body">
												<div className="text-base font-bold leading-none text-[#365561]">
													{day}
												</div>

												<div className="mt-0.5 text-[10px] font-semibold text-gray-700 availability-weekday">
													{weekday}
												</div>

												<div className="mt-0.5 truncate text-[10px] text-gray-500 availability-time">
													{startTime} - {endTime}
												</div>
											</div>

									 		{/* {(isSelected || event.isCallRequested) && (
												<div className="slot-selected-overlay no-print">
													<div className="slot-selected-check">
														✓
													</div>
												</div>
											)} */}

											{isSelected && (
												<div className="slot-selected-overlay no-print">
													<div className="slot-selected-check">
														✓
													</div>
												</div>
											)}
											{event.isCallRequested && (
												<div className="slot-selected-overlay no-print">
													<div className="text-white/60 text-xs font-semibold -rotate-45 whitespace-nowrap">
														Call Requested
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
							{selectedSlots.length > 0 && (
								<button
									type="button"
									className="mt-2 px-4 py-2 rounded-xl bg-[#365561]/20 no-print"
									onClick={() => requestForCall()}
									disabled={loading}
								>
									{loading ? <i class="fa-solid fa-spinner"></i> : 'Request call'}
								</button>
							)}
						</div>

          </div>
        </div>
      </div>
    </div>
  );

  const renderScreenLayout = () => (
    <div className="pt-4 sm:pt-36 pb-10 grid grid-cols-1 lg:grid-cols-6 gap-6 print-single-column print-gap-reset print-pt-reset print-px-reset print-stack-gap">
      <section className="col-span-3 space-y-6">
        {renderSummarySection()}
        {renderSpiderChartSection()}
        {renderScreeningQuestionsSection()}
      </section>

      <aside className="col-span-3 space-y-6">
        {renderExperienceSection()}
        {renderEducationSection()}
      </aside>
    </div>
  );

  const renderPrintLayout = () => (
    <div className="pt-4 pb-10 space-y-4 print-pt-reset print-px-reset print-stack-gap">
      {renderSummarySection()}
      {renderExperienceSection()}
      {renderEducationSection()}
      {renderScreeningQuestionsSection()}
      {renderSpiderChartSection()}
    </div>
  );

  return (
    <div ref={ref} className="print-root bg-none">
      <style>
				{`
					.profile-print-only {
						display: none;
					}

					.slot-selected-overlay {
						position: absolute;
						inset: 0;
						z-index: 30;
						display: flex;
						align-items: center;
						justify-content: center;
						background: rgba(54, 85, 97, 0.24);
						pointer-events: none;
					}

					.slot-selected-check {
						width: 30px;
						height: 30px;
						border-radius: 999px;
						display: flex;
						align-items: center;
						justify-content: center;
						background: rgba(54, 85, 97, 0.50);
						// color: #ffffff;
						color: rgb(245, 247, 247, 0.6);
						font-size: 19px;
						line-height: 1;
						font-weight: 800;
						box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25);
					}

					@media print {
						.profile-screen-only {
							display: none !important;
						}

						.profile-print-only {
							display: block !important;
						}

						.no-print {
							display: none !important;
						}
					}
				`}
			</style>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8">
        {renderHeaderSection()}

        <div className="profile-screen-only">{renderScreenLayout()}</div>

        <div className="profile-print-only">{renderPrintLayout()}</div>
      </div>
    </div>
  );
});

export default ProfilePrintContent;